import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { fetchOrdersMeta } from './api';
import CreditBatchesPanel from './CreditBatchesPanel';
import CreditOrdersPanel from './CreditOrdersPanel';
import OrgBalancesPanel from './OrgBalancesPanel';
import OrgLedgerPanel from './OrgLedgerPanel';
import ProductOrdersPanel from './ProductOrdersPanel';
import { ORDERS_SUBTAB_CAPS, OrdersMeta } from './types';

type SubtabKey = 'credits' | 'batches' | 'products' | 'balances' | 'ledger';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  { key: 'credits', label: 'Credit Orders', cap: ORDERS_SUBTAB_CAPS.credits },
  { key: 'batches', label: 'Credit Batches', cap: ORDERS_SUBTAB_CAPS.batches },
  { key: 'products', label: 'Product Orders', cap: ORDERS_SUBTAB_CAPS.products },
  { key: 'balances', label: 'Org Balances', cap: ORDERS_SUBTAB_CAPS.balances },
  { key: 'ledger', label: 'Org Ledger', cap: ORDERS_SUBTAB_CAPS.ledger },
];

const AdminOrdersTab: React.FC = () => {
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('orders', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);
  const [meta, setMeta] = useState<OrdersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrdersMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load orders admin data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (visibleSubtabs.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any orders tools in this panel.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {visibleSubtabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              active === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {!loading && !error && meta && active === 'credits' && <CreditOrdersPanel meta={meta} />}
      {!loading && !error && meta && active === 'batches' && <CreditBatchesPanel meta={meta} />}
      {!loading && !error && meta && active === 'products' && <ProductOrdersPanel meta={meta} />}
      {!loading && !error && meta && active === 'balances' && <OrgBalancesPanel meta={meta} />}
      {!loading && !error && meta && active === 'ledger' && <OrgLedgerPanel meta={meta} />}
    </div>
  );
};

export default AdminOrdersTab;
